import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { MessageCircle, TrendingUp } from 'lucide-react';
import { useOnChainStats } from '../../hooks/useOnChainStats';

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    green: '#22c55e',
    red: '#ef4444',
    orange: '#FF8C00',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(181, 111, 111, 0.2)',
};

const cardStyle: React.CSSProperties = {
    background: THEME.card,
    border: `1px solid ${THEME.border}`,
    borderRadius: '20px',
    overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
};

const bodyStyle: React.CSSProperties = {
    padding: '1.5rem',
};

const SentimentPanel: React.FC<{ tokenAddress?: string }> = ({ tokenAddress }) => {
    const stats = useOnChainStats(tokenAddress, 900_000);
    const [sentimentHistory, setSentimentHistory] = useState<{ time: string; score: number }[]>([]);

    // Reset accumulated history when token changes
    useEffect(() => {
        setSentimentHistory([]);
    }, [tokenAddress]);

    // Calculate sentiment from real data
    const sentimentScore = React.useMemo(() => {
        if (!stats) return 50;
        let score = 50;
        const buys = stats.buys24h || 0;
        const sells = stats.sells24h || 0;
        if (buys + sells > 0) {
            const ratio = buys / (sells || 1);
            if (ratio > 2) score += 25;
            else if (ratio > 1.3) score += 15;
            else if (ratio > 1) score += 5;
            else if (ratio < 0.5) score -= 25;
            else if (ratio < 0.7) score -= 15;
            else if (ratio < 1) score -= 5;
        }
        return Math.max(0, Math.min(100, Math.round(score)));
    }, [stats]);

    const sentimentLabel = sentimentScore >= 65 ? 'BULLISH' : sentimentScore <= 35 ? 'BEARISH' : 'NEUTRAL';
    const sentimentColor = sentimentScore >= 65 ? THEME.green : sentimentScore <= 35 ? THEME.red : THEME.yellow;

    // Track sentiment over time
    useEffect(() => {
        setSentimentHistory(prev => {
            const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const updated = [...prev, { time: now, score: sentimentScore }];
            return updated.slice(-20);
        });
    }, [sentimentScore]);

    // Gauge data for PieChart
    const gaugeData = [
        { name: 'Score', value: sentimentScore },
        { name: 'Remaining', value: 100 - sentimentScore },
    ];

    const buys = stats?.buys24h || 0;
    const sells = stats?.sells24h || 0;
    const buySellData = [
        { name: 'Buys', value: buys, color: THEME.green },
        { name: 'Sells', value: sells, color: THEME.red },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <MessageCircle size={24} color={THEME.purple} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>Social Sentiment</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Sentiment Gauge */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <TrendingUp size={18} color={sentimentColor} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Sentiment Score</h3>
                    </div>
                    <div style={{ ...bodyStyle, textAlign: 'center' }}>
                        <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                                <Pie
                                    data={gaugeData}
                                    cx="50%"
                                    cy="80%"
                                    startAngle={180}
                                    endAngle={0}
                                    innerRadius={60}
                                    outerRadius={80}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    <Cell fill={sentimentColor} />
                                    <Cell fill="rgba(255,255,255,0.05)" />
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{ marginTop: '-2rem' }}>
                            <span style={{ fontSize: '2.5rem', fontWeight: 800, color: sentimentColor }}>{sentimentScore}</span>
                            <span style={{ fontSize: '1rem', color: '#6b7280' }}>/100</span>
                        </div>
                        <div style={{
                            marginTop: '0.5rem', padding: '0.25rem 1rem', display: 'inline-block',
                            background: `${sentimentColor}20`, border: `1px solid ${sentimentColor}40`,
                            borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 700, color: sentimentColor,
                        }}>
                            {sentimentLabel}
                        </div>
                    </div>
                </div>

                {/* Buy/Sell Distribution */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <TrendingUp size={18} color={THEME.green} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>24h Buy/Sell</h3>
                    </div>
                    <div style={{ ...bodyStyle, textAlign: 'center' }}>
                        {buys + sells > 0 ? (
                            <>
                                <ResponsiveContainer width="100%" height={140}>
                                    <PieChart>
                                        <Pie
                                            data={buySellData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={60}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {buySellData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '0.5rem' }}>
                                    <div>
                                        <div style={{ color: THEME.green, fontWeight: 700, fontSize: '1.25rem' }}>{buys}</div>
                                        <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Buys</div>
                                    </div>
                                    <div>
                                        <div style={{ color: THEME.red, fontWeight: 700, fontSize: '1.25rem' }}>{sells}</div>
                                        <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Sells</div>
                                    </div>
                                    <div>
                                        <div style={{ color: THEME.yellow, fontWeight: 700, fontSize: '1.25rem' }}>{(buys / (sells || 1)).toFixed(2)}</div>
                                        <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Ratio</div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p style={{ color: '#6b7280', padding: '3rem 0' }}>Loading transaction data...</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Sentiment Trend */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <TrendingUp size={18} color={THEME.purple} />
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Sentiment Trend</h3>
                </div>
                <div style={bodyStyle}>
                    {sentimentHistory.length > 1 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={sentimentHistory}>
                                <defs>
                                    <linearGradient id="sentimentGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={THEME.purple} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={THEME.purple} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="time" stroke="#6b7280" fontSize={10} />
                                <YAxis domain={[0, 100]} stroke="#6b7280" fontSize={12} />
                                <Tooltip
                                    contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                    labelStyle={{ color: '#fff' }}
                                />
                                <Area type="monotone" dataKey="score" stroke={THEME.purple} fill="url(#sentimentGrad)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>Collecting sentiment data...</p>
                    )}
                </div>
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                {[
                    { label: 'Volume 24h', value: stats?.volume24h ? `$${(stats.volume24h / 1000).toFixed(1)}K` : '...', color: THEME.purple },
                    { label: 'Liquidity', value: stats?.liquidity ? `$${(stats.liquidity / 1000).toFixed(1)}K` : '...', color: THEME.green },
                    { label: 'Market Cap', value: stats?.marketCap ? `$${(stats.marketCap / 1000).toFixed(1)}K` : '...', color: THEME.yellow },
                    { label: 'Holders', value: stats?.holders?.toLocaleString() || '...', color: THEME.orange },
                ].map((stat, i) => (
                    <div key={i} style={{
                        background: `${stat.color}10`, border: `1px solid ${stat.color}25`,
                        borderRadius: '16px', padding: '1rem', textAlign: 'center',
                    }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: stat.color }}>{stat.value}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SentimentPanel;
